import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowUp, ArrowDown, Save, AlertTriangle, Plus, Trash2, KeyRound } from "lucide-react";

const slug = (t) => (t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/_+/g, "_");

export const GruppiTipiConfig = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["tipi-gruppi"], queryFn: async () => (await api.get("/tipi-pratica/gruppi")).data });
  const [righe, setRighe] = useState([]);
  const [salvo, setSalvo] = useState(false);
  const [nuova, setNuova] = useState(null);

  useEffect(() => { if (data) setRighe(data.map((g, i) => ({ ...g, ordine: i }))); }, [data]);

  const sposta = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= righe.length) return;
    const copia = [...righe];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    setRighe(copia.map((r, k) => ({ ...r, ordine: k })));
  };

  const salva = async () => {
    setSalvo(true);
    try {
      await api.put("/admin/gruppi-tipi", { gruppi: righe.map((r, i) => ({ key: r.key, label: r.label, ordine: i })) });
      await qc.invalidateQueries({ queryKey: ["tipi-gruppi"] });
      toast.success("Voci aggiornate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSalvo(false); }
  };

  const aggiungi = async () => {
    try {
      await api.post("/admin/gruppi-tipi", { label: nuova.label, key: nuova.key });
      await qc.invalidateQueries({ queryKey: ["tipi-gruppi"] });
      setNuova(null);
      toast.success("Voce aggiunta");
    } catch (e) { toast.error(e?.response?.data?.detail || "Voce non aggiunta"); }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Elimino la voce «${r.label}»?`)) return;
    try {
      await api.delete(`/admin/gruppi-tipi/${r.key}`);
      await qc.invalidateQueries({ queryKey: ["tipi-gruppi"] });
      toast.success("Voce eliminata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Voce non eliminata"); }
  };

  const cambiaChiave = async (r) => {
    const proposta = window.prompt(`Nuova chiave per «${r.label}» (minuscole e underscore).\nVerrebbero riassegnati ${r.n_tipi} tipi di pratica.`, r.key);
    if (!proposta) return;
    if (!window.confirm(`Confermi? La chiave diventa «${slug(proposta)}» e ${r.n_tipi} tip${r.n_tipi === 1 ? "o" : "i"} di pratica vengono riassegnati in un'unica operazione.`)) return;
    try {
      const { data } = await api.post(`/admin/gruppi-tipi/${r.key}/cambia-chiave`, { nuova_chiave: slug(proposta) });
      await qc.invalidateQueries({ queryKey: ["tipi-gruppi"] });
      await qc.invalidateQueries({ queryKey: ["tipi-pratica"] });
      toast.success(`Chiave cambiata: ${data.tipi_riassegnati} tipi riassegnati`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Chiave non cambiata"); }
  };

  const vuoti = righe.filter((r) => r.n_tipi === 0);

  return (
    <div data-testid="gruppi-tipi-config" className="bg-white border border-[#1A2B4A]/10 rounded-sm p-6">
      <h2 className="text-base md:text-lg font-semibold text-[#1A2B4A]">Voci di «Di cosa ha bisogno il cliente?»</h2>
      <p className="text-[12px] text-[#4a5568] mt-1 mb-4">
        La prima domanda del wizard «Nuova pratica». Puoi cambiare le etichette e l'ordine: la chiave resta
        quella con cui i tipi sono già collegati. Una voce senza tipi non viene mostrata al cliente.
      </p>

      {vuoti.length > 0 && (
        <div data-testid="gruppi-vuoti-alert" className="mb-4 flex items-start gap-2 rounded-md border border-[#C9A84C]/50 bg-[#fdfaf1] px-3 py-2 text-[12px] text-[#7a5c12]">
          <AlertTriangle className="w-4 h-4 mt-[1px] shrink-0" />
          <span>
            {vuoti.length === 1 ? "Questa voce non contiene nessun tipo e resta nascosta nel wizard: " : "Queste voci non contengono nessun tipo e restano nascoste nel wizard: "}
            <strong>{vuoti.map((v) => v.label).join(", ")}</strong>. Assegnale un tipo dalla scheda del tipo pratica.
          </span>
        </div>
      )}

      <div className="space-y-2">
        {righe.map((r, i) => (
          <div key={r.key} data-testid={`gruppo-riga-${r.key}`} className="flex items-center gap-2 border border-[#1A2B4A]/10 rounded-md px-3 py-2">
            <div className="flex flex-col">
              <button data-testid={`gruppo-su-${r.key}`} onClick={() => sposta(i, -1)} disabled={i === 0}
                className="text-[#1A2B4A]/50 hover:text-[#C9A84C] disabled:opacity-25"><ArrowUp className="w-3 h-3" /></button>
              <button data-testid={`gruppo-giu-${r.key}`} onClick={() => sposta(i, 1)} disabled={i === righe.length - 1}
                className="text-[#1A2B4A]/50 hover:text-[#C9A84C] disabled:opacity-25"><ArrowDown className="w-3 h-3" /></button>
            </div>
            <input data-testid={`gruppo-label-${r.key}`} value={r.label}
              onChange={(e) => setRighe(righe.map((x) => x.key === r.key ? { ...x, label: e.target.value } : x))}
              className="flex-1 px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]" />
            <code data-testid={`gruppo-key-${r.key}`} className="text-[11px] text-[#8a93a6] w-44 shrink-0">{r.key}</code>
            <span data-testid={`gruppo-conteggio-${r.key}`}
              className={`text-[12px] w-20 shrink-0 text-right ${r.n_tipi === 0 ? "text-[#9b2c2c]" : "text-[#4a5568]"}`}>
              {r.n_tipi} {r.n_tipi === 1 ? "tipo" : "tipi"}
            </span>
            {r.n_tipi === 0 && (
              <span data-testid={`gruppo-vuoto-${r.key}`} title="Voce senza tipi: non compare nel wizard"
                className="inline-flex items-center gap-1 text-[11px] text-[#7a5c12] bg-[#fdfaf1] border border-[#C9A84C]/40 rounded px-2 py-[2px] shrink-0">
                <AlertTriangle className="w-3 h-3" /> vuota
              </span>
            )}
            <button data-testid={`gruppo-cambia-chiave-${r.key}`} onClick={() => cambiaChiave(r)} title="Cambia chiave e riassegna i tipi"
              className="text-[#1A2B4A]/50 hover:text-[#C9A84C]"><KeyRound className="w-4 h-4" /></button>
            <button data-testid={`gruppo-elimina-${r.key}`} onClick={() => elimina(r)} title="Elimina voce (solo se vuota)"
              className="text-[#9b2c2c]/50 hover:text-[#9b2c2c]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {nuova ? (
        <div data-testid="gruppo-nuova-form" className="mt-4 border border-[#C9A84C]/50 rounded-md p-3 bg-[#fdfaf1]">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Etichetta</label>
              <input data-testid="gruppo-nuova-label" autoFocus value={nuova.label}
                onChange={(e) => setNuova({ label: e.target.value, key: nuova.manuale ? nuova.key : slug(e.target.value), manuale: nuova.manuale })}
                placeholder="es. Lavoro e assunzioni"
                className="w-full mt-1 px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Chiave (si scrive solo ora)</label>
              <input data-testid="gruppo-nuova-key" value={nuova.key}
                onChange={(e) => setNuova({ ...nuova, key: slug(e.target.value), manuale: true })}
                placeholder="lavoro_assunzioni"
                className="w-full mt-1 px-3 py-2 text-sm font-mono border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button data-testid="gruppo-nuova-salva" onClick={aggiungi} disabled={!nuova.label || !nuova.key}
              className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-md hover:bg-[#22375c] disabled:opacity-50">Crea voce</button>
            <button data-testid="gruppo-nuova-annulla" onClick={() => setNuova(null)}
              className="px-4 py-2 text-sm border border-[#1A2B4A]/25 rounded-md hover:bg-white">Annulla</button>
          </div>
        </div>
      ) : (
        <button data-testid="gruppo-aggiungi" onClick={() => setNuova({ label: "", key: "", manuale: false })}
          className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#1A2B4A]/25 rounded-md hover:bg-[#f9f8f5]">
          <Plus className="w-4 h-4" /> Aggiungi voce
        </button>
      )}

      <br />
      <button data-testid="gruppi-salva" onClick={salva} disabled={salvo}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-md hover:bg-[#22375c] disabled:opacity-60">
        {salvo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva voci
      </button>
    </div>
  );
};

export default GruppiTipiConfig;
